import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UserAvatar } from "../../components/ui/avatar";
import { UserIdentity } from "../../components/UserIdentity";
import { useAuth } from "../../app/useAuth";
import { useSharedProfileData } from "../../hooks/useSharedProfileData";
import { useEnrichedSchedulers } from "../../hooks/useEnrichedSchedulers";
import { useSharedBasicPolls } from "../../hooks/useSharedBasicPolls";
import { useUserProfiles } from "../../hooks/useUserProfiles";
import { resolveIdentifier } from "../../lib/identifiers";
import { LoadingState } from "../../components/ui/spinner";
import { SharedProfileContent } from "../friends/SharedProfileContent";

/**
 * Full-page profile view (alternative to modal, toggled by feature flag).
 * Route: /profile/:identifier
 * Privacy: only shows shared polls/groups/mutual friends.
 */
export default function PublicProfilePage() {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [targetUser, setTargetUser] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    if (!identifier) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    setResolveError(null);

    resolveIdentifier(decodeURIComponent(identifier))
      .then((resolved) => {
        if (!cancelled) {
          setTargetUser({
            email: resolved.email,
            ...(resolved.userData || {}),
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setResolveError(err.message || "User not found");
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [identifier]);

  const email = targetUser?.email;
  const { sharedPolls, sharedGroups, mutualFriends, getGroupColor, groupsById, loading } =
    useSharedProfileData(email);
  const { enrichUsers } = useUserProfiles(mutualFriends);
  const mutualFriendProfiles = enrichUsers(mutualFriends);
  const { enrichedSchedulers, loading: enrichLoading } =
    useEnrichedSchedulers(sharedPolls, currentUser?.uid);
  const sharedGroupIds = useMemo(
    () => sharedGroups.map((g) => g.id),
    [sharedGroups]
  );
  const { sharedBasicPolls, loading: basicPollsLoading } =
    useSharedBasicPolls({ sharedGroupIds, groupsById, getGroupColor });

  if (resolving) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState message="Loading profile..." />
      </div>
    );
  }

  if (resolveError || !targetUser) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
          User not found
        </h2>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {resolveError || "Could not find this user."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-4 rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const isLoading = loading || enrichLoading || basicPollsLoading;

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <div className="rounded-3xl bg-white p-6 shadow-xl shadow-slate-200 dark:bg-slate-900 dark:shadow-slate-900/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserAvatar
              user={targetUser}
              email={email}
              src={targetUser.avatar || targetUser.photoURL}
              size={48}
            />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                <UserIdentity user={targetUser} />
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
          >
            Back
          </button>
        </div>

        {isLoading ? (
          <div className="mt-6">
            <LoadingState message="Loading shared data..." />
          </div>
        ) : (
          <SharedProfileContent
            sharedPolls={enrichedSchedulers}
            sharedGroups={sharedGroups}
            sharedBasicPolls={sharedBasicPolls}
            mutualFriendProfiles={mutualFriendProfiles}
            getGroupColor={getGroupColor}
            groupsById={groupsById}
          />
        )}
      </div>
    </div>
  );
}
